'use client';

import { c as _c } from "react/compiler-runtime";
import { XMarkIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { BadgeShareSection } from '@src/components/BadgeShare/BadgeShareSection';
import { EarnedRibbon } from '@src/components/Badges/EarnedRibbon';
import { Spinner } from '@src/components/Spinner/Spinner';
import { useBadgeDetail } from '@src/hooks/useBadgeDetail';
import { useLocale } from '@src/i18n/LocaleProvider';
import { getEarnedRibbonDisplayCount, hasBadgeShareContent } from '@src/utils/gamification.util';
import { cn } from '@src/utils/tailwind';
interface BadgeShareContentProps {
  badge: GamificationApi.Badge;
  initialDetail?: GamificationApi.Badge;
  onClose: () => void;
}
export function BadgeShareContent(t0) {
  const $ = _c(59);
  const {
    badge,
    initialDetail,
    onClose
  } = t0;
  const {
    t
  } = useLocale("badge-detail");
  const prefetchedDetail = initialDetail ?? (hasBadgeShareContent(badge) ? badge : undefined);
  const shouldFetch = !prefetchedDetail;
  let t1;
  if ($[0] !== badge || $[1] !== shouldFetch) {
    t1 = {
      badge,
      enabled: shouldFetch
    };
    $[0] = badge;
    $[1] = shouldFetch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: fetchedDetail,
    isLoading,
    isError
  } = useBadgeDetail(t1);
  const detail = prefetchedDetail ?? fetchedDetail ?? badge;
  let t2;
  if ($[3] !== detail) {
    t2 = getEarnedRibbonDisplayCount(detail);
    $[3] = detail;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const earnedRibbonCount = t2;
  const showEarnedRibbon = earnedRibbonCount != null;
  const socialShare = detail.content?.badgeSocialShare;
  const backgroundUrl = detail.content?.badgeBackgroundTwo;
  const subtitle = detail.content?.badgeCelebrationCopy;
  if (shouldFetch && isLoading && !fetchedDetail) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex min-h-[320px] items-center justify-center bg-bodi-black-text lg:h-full"><Spinner size="large" className="text-white" /></div>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (shouldFetch && isError && !hasBadgeShareContent(detail)) {
    let t3;
    if ($[6] !== t) {
      t3 = t("share-load-error");
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t3) {
      t4 = <p className="text-body-sm text-bodi-gray-medium">{t3}</p>;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t) {
      t5 = t("back");
      $[10] = t;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== onClose || $[13] !== t5) {
      t6 = <button type="button" onClick={onClose} className="text-body-sm font-medium text-bodi-accent-bright-500">{t5}</button>;
      $[12] = onClose;
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t4 || $[16] !== t6) {
      t7 = <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 bg-bodi-black-text p-6 text-white lg:h-full">{t4}{t6}</div>;
      $[15] = t4;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  const t3 = !backgroundUrl && "bg-bodi-black-text";
  let t4;
  if ($[18] !== t3) {
    t4 = cn("relative flex w-full flex-col overflow-hidden text-white", "min-h-[640px] lg:h-full", t3);
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== backgroundUrl) {
    t5 = backgroundUrl && <div className="pointer-events-none absolute inset-0"><Image src={backgroundUrl} alt="" fill={true} priority={true} sizes="484px" className="object-cover object-top" /></div>;
    $[20] = backgroundUrl;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t) {
    t6 = t("back");
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <XMarkIcon className="size-6 stroke-[2px] text-white" />;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== onClose || $[26] !== t6) {
    t8 = <button type="button" onClick={onClose} className="absolute top-[21px] right-[21px] z-10 flex size-[30px] cursor-pointer items-center justify-center" aria-label={t6}>{t7}</button>;
    $[25] = onClose;
    $[26] = t6;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== detail.name || $[29] !== detail.url) {
    t9 = <div className="relative aspect-square w-[170px] shrink-0 overflow-hidden rounded-[11px] md:w-[200px]">{detail.url ? <Image src={detail.url} alt={detail.name} fill={true} sizes="(min-width: 768px) 200px, 170px" className="object-cover" /> : <div className="size-full bg-bodi-gray-dark" />}</div>;
    $[28] = detail.name;
    $[29] = detail.url;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== detail.name) {
    t10 = <h3 className="text-heading-display-3 m-0">{detail.name}</h3>;
    $[31] = detail.name;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== detail.status || $[34] !== earnedRibbonCount || $[35] !== showEarnedRibbon) {
    t11 = showEarnedRibbon && <EarnedRibbon earnedCount={earnedRibbonCount} status={detail.status} />;
    $[33] = detail.status;
    $[34] = earnedRibbonCount;
    $[35] = showEarnedRibbon;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11) {
    t12 = <div className="flex w-full flex-col items-center gap-1">{t10}{t11}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== subtitle) {
    t13 = subtitle && <p className="m-0 w-full text-body-base">{subtitle}</p>;
    $[40] = subtitle;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13) {
    t14 = <div className="flex w-full flex-col items-center gap-4">{t12}{t13}</div>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t14 || $[46] !== t9) {
    t15 = <div className="flex w-full flex-col items-center gap-16">{t9}{t14}</div>;
    $[45] = t14;
    $[46] = t9;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== socialShare) {
    t16 = <BadgeShareSection socialShare={socialShare} />;
    $[48] = socialShare;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Image src="/images/bodi_white.svg" alt="BODi" width={100} height={20} className="h-5 w-[100px]" />;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t15 || $[52] !== t16) {
    t18 = <div className="relative z-[1] flex w-full flex-1 flex-col items-center px-6 pt-20 pb-10 text-center lg:px-16 lg:pt-[140px] lg:pb-16"><div className="flex w-full max-w-[356px] flex-col items-center gap-8">{t15}{t16}{t17}</div></div>;
    $[51] = t15;
    $[52] = t16;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t18 || $[55] !== t4 || $[56] !== t5 || $[57] !== t8) {
    t19 = <div className={t4}>{t5}{t8}{t18}</div>;
    $[54] = t18;
    $[55] = t4;
    $[56] = t5;
    $[57] = t8;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}